import { Col, Descriptions, Row } from 'antd';
import { get } from 'lodash';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useQuery } from 'react-query';
import { formatNumberToLocale } from 'utils/numbers';

import { handleApiErrors } from '../../api/axiosInstance';
import PromptsAPI from '../../api/prompts';
import { Button } from '../../components';
import Box from '../../components/Box';
import PageBreadcrumbs from '../../components/PageBreadcrumbs';
import SpaceSpinner from '../../components/SpaceSpinner';
import PageContainer from '../../containers/PageContainer';
import { INTERNAL_LINKS } from '../../enum';
import withAuthentication from '../../hocs/withAuthentication';
import { formatPageTitle } from '../../utils/common';

const DescriptionsView = ({ t, prompt }) => (
  <Descriptions column={1} bordered size="small">
    <Descriptions.Item label={t('name')}>{get(prompt, 'name', '-')}</Descriptions.Item>
    <Descriptions.Item label={t('description')}>
      {get(prompt, 'description', '-')}
    </Descriptions.Item>
    <Descriptions.Item label={t('model')}>{get(prompt, 'model', '-')}</Descriptions.Item>
    <Descriptions.Item label={t('type')}>{t(get(prompt, 'type', '-'))}</Descriptions.Item>
    <Descriptions.Item label={t('format')}>{get(prompt, 'format', '-')}</Descriptions.Item>
    <Descriptions.Item label={t('active')}>
      {get(prompt, 'isActive') ? t('yes') : t('no')}
    </Descriptions.Item>
    <Descriptions.Item label={t('text')} contentStyle={{ whiteSpace: 'pre-wrap' }}>
      {get(prompt, 'text', '-')}
    </Descriptions.Item>
  </Descriptions>
);

const PromptDetailsView = props => {
  const { t, history, match } = props;
  const promptId = get(match, 'params.id');

  const promptQuery = useQuery({
    enabled: !!promptId,
    queryKey: ['fetchPromptDetails', promptId],
    queryFn: () => PromptsAPI.fetchPrompt(promptId),
    onError: error => {
      handleApiErrors(error.response);
      history.push(INTERNAL_LINKS.PROMPTS);
    },
  });

  const prompt = promptQuery.data;
  const generationConfig = get(prompt, 'generationConfig', {});

  return (
    <PageContainer
      sideActionComponent={
        !promptQuery.isFetching && prompt ? (
          <Button size="sm" onClick={() => history.push(INTERNAL_LINKS.UPDATE_PROMPT(promptId))}>
            Edit
          </Button>
        ) : null
      }
      title={
        <PageBreadcrumbs
          items={[
            { label: t('prompts'), onClick: () => history.push(INTERNAL_LINKS.PROMPTS) },
            { label: get(prompt, 'name', ' ') },
          ]}
        />
      }
    >
      <Helmet>
        <title>{formatPageTitle('Prompt Details')}</title>
      </Helmet>

      <Row justify="center">
        <Col flex="832px">
          <Box>
            {promptQuery.isFetching || !prompt ? (
              <SpaceSpinner />
            ) : (
              <DescriptionsView t={t} prompt={prompt} />
            )}
          </Box>
        </Col>
      </Row>

      {!promptQuery.isFetching && prompt && (
        <Row justify="center" style={{ marginTop: 12 }}>
          <Col flex="832px">
            <Box>
              <Descriptions title={t('generationConfig')} column={1} bordered size="small">
                <Descriptions.Item label={t('temperature')}>
                  {formatNumberToLocale(generationConfig.temperature) ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('topK')}>
                  {formatNumberToLocale(generationConfig.topK) ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('topP')}>
                  {formatNumberToLocale(generationConfig.topP) ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('maxOutputTokens')}>
                  {formatNumberToLocale(generationConfig.maxOutputTokens) ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            </Box>
          </Col>
        </Row>
      )}
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(PromptDetailsView));
